import React from "react";
import { Alert, Box, Chip, IconButton, Paper, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { BarPlot } from "@mui/x-charts/BarChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import { PageContainer, SectionHeader, DataTable, StatCard } from "../../Layout/Common";
import { gs, Loader, StaleHint, ensureRegions, regionLabel, Cut, FIXED, ELLIPSIS, n, usdH, usdDay, branchShort, openPage } from "./servers";

// Game Servers → Costs (§14 `costs`, §16): what the title's machines cost — spend per day / region / provider (the
// destroyed machines' `gsMachineLog` rows plus the live machines' hours so far), the live hourly burn, and per region the
// dedicated break-even: the always-on load (p10 of the seats used over 30 days, in machines) at the hourly price vs the
// monthly price of a candidate dedicated spec (the scale rule's `breakEvenMonthlyUsd`) — the page says when a dedicated
// server starts paying for itself (owner decision D7: dedicated servers come later, when the player count justifies them).

const RANGES = [7, 30, 90];
const HOURS_PER_MONTH = 730;
const COLORS = ["#1976d2", "#2e7d32", "#ed6c02", "#6a1b9a", "#d32f2f", "#00838f", "#5d4037", "#c2185b"];
const usd2 = (v) => `$${Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usdMonth = (v) => `$${Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo`;
const fx = (v, d = 1) => Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: d });

// Every UTC day from `from` to `to` ("yyyy-MM-dd"), so a day with no spend still has its bar
function dayRange(from, to, days) {
    const out = [];
    const end = to ? new Date(`${String(to).slice(0, 10)}T00:00:00Z`) : new Date();
    const start = from ? new Date(`${String(from).slice(0, 10)}T00:00:00Z`) : new Date(end.getTime() - (days - 1) * 86400000);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return out;
    for (let t = start.getTime(); t <= end.getTime() && out.length < 400; t += 86400000) out.push(new Date(t).toISOString().slice(0, 10));
    return out;
}

export default class CostsPage extends React.Component {
    state = { days: 30, costs: null, netRegions: null, loadedAt: null, stale: null };

    loader = new Loader(this, async () => {
        ensureRegions(this);
        const costs = await gs("Loading costs", ["costs"], { payload: { days: this.state.days }, silent: true });
        return { costs };
    }, 60000);

    componentDidMount() { this.loader.start(); }
    componentWillUnmount() { this.loader.stop(); }

    setDays = (_, days) => { if (days) this.setState({ days, costs: null }, this.loader.reload); };

    renderCards() {
        const c = this.state.costs;
        const live = c.live ?? {};
        const days = c.days ?? this.state.days;
        return <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <StatCard label="Live burn" value={usdH(live.usdPerHour)} secondary={`≈ ${usdDay(live.usdPerHour)} · ≈ ${usdMonth((live.usdPerHour ?? 0) * HOURS_PER_MONTH)}`} />
            <StatCard label="Machines live" value={n(live.machines)} secondary="every machine of the title" />
            <StatCard label={`Spend, ${days} days`} value={usd2(c.totalUsd)} secondary={`≈ ${usd2((c.totalUsd ?? 0) / Math.max(1, days))} per day`} />
        </Box>;
    }

    renderChart() {
        const c = this.state.costs;
        const daily = c.daily ?? [];
        const days = dayRange(c.from, c.to, c.days ?? this.state.days);
        const regions = [...new Set(daily.map(d => d.region ?? "?"))].sort();
        if (days.length === 0 || regions.length === 0) return <Paper sx={{ p: 2, mb: 2 }}><Typography variant="body2" color="text.secondary">No spend in this range.</Typography></Paper>;
        const sum = new Map();
        for (const d of daily) {
            const k = `${String(d.day).slice(0, 10)}|${d.region ?? "?"}`;
            sum.set(k, (sum.get(k) ?? 0) + Number(d.usd ?? 0));
        }
        const every = Math.max(1, Math.round(days.length / 15));
        return <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Spend per day (UTC) by region, USD</Typography>
            <ChartContainer height={260}
                            xAxis={[{ id: "day", scaleType: "band", data: days.map(d => d.slice(5)), tickLabelStyle: { fontSize: 10 }, tickInterval: (_, i) => i % every === 0 }]}
                            yAxis={[{ id: "usd", min: 0 }]}
                            series={regions.map((r, i) => ({ type: "bar", id: `r-${r}`, yAxisId: "usd", stack: "usd", label: regionLabel(r, this.state.netRegions),
                                                             color: COLORS[i % COLORS.length], data: days.map(d => +(sum.get(`${d}|${r}`) ?? 0).toFixed(2)),
                                                             valueFormatter: v => usd2(v) }))}>
                <BarPlot />
                <ChartsXAxis axisId="day" />
                <ChartsYAxis axisId="usd" />
                <ChartsTooltip />
                <ChartsLegend />
            </ChartContainer>
        </Paper>;
    }

    renderLive() {
        const { costs: c, netRegions } = this.state;
        const rows = c.live?.rows ?? [];
        const columns = [
            { id: "region", label: "Region", width: 200, render: r => <Cut text={regionLabel(r.region, netRegions)} /> },
            { id: "provider", label: "Provider", width: 110, render: r => <Cut text={r.provider} /> },
            { id: "machines", label: "Machines", width: 90, align: "right", render: r => n(r.machines) },
            { id: "hour", label: "Per hour", width: 100, align: "right", render: r => usdH(r.usdPerHour) },
            { id: "day", label: "≈ per day", width: 100, align: "right", render: r => usd2((r.usdPerHour ?? 0) * 24) },
            { id: "month", label: "≈ per month", width: 110, align: "right", render: r => usdMonth((r.usdPerHour ?? 0) * HOURS_PER_MONTH) },
        ];
        return <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Live burn</Typography>
            {rows.length === 0
                ? <Typography variant="body2" color="text.secondary">No live machine.</Typography>
                : <DataTable columns={columns} rows={rows} getRowKey={r => `${r.region}|${r.provider}`} sx={{ ...FIXED, maxWidth: 760 }} />}
        </Box>;
    }

    renderBreakEven() {
        const { costs: c, netRegions } = this.state;
        const rows = c.breakEven ?? [];
        const verdict = (b) => {
            const perMachineMonth = (b.hourlyUsd ?? 0) * HOURS_PER_MONTH;
            if (!(b.dedicatedMonthlyUsd > 0)) return <Typography variant="body2" color="text.secondary">No candidate: set "Dedicated candidate" ($/month) on the region's scale rule.</Typography>;
            if (!(perMachineMonth > 0)) return <Typography variant="body2" color="text.secondary">No hourly price for the region's plan.</Typography>;
            const machines = b.dedicatedMonthlyUsd / perMachineMonth;
            const seats = b.seatsPerMachine > 0 ? ` (≈ ${n(Math.round(machines * b.seatsPerMachine))} seats at p10)` : "";
            if (b.pays) return <Box>
                <Chip size="small" color="success" label="dedicated pays now" sx={{ mb: 0.25 }} />
                <Typography variant="body2">Saves ≈ {usdMonth((b.alwaysOnMonthlyUsd ?? 0) - b.dedicatedMonthlyUsd)}: the always-on load is above {fx(machines)} machines{seats}.</Typography>
            </Box>;
            return <Box>
                <Chip size="small" variant="outlined" label="hourly is cheaper" sx={{ mb: 0.25 }} />
                <Typography variant="body2">A dedicated pays for itself from {fx(machines)} machines always on{seats}; now {fx(b.machinesP10)}.</Typography>
            </Box>;
        };
        const columns = [
            { id: "region", label: "Region · rule", width: 170, render: b => <>
                <Box sx={{ ...ELLIPSIS, fontWeight: 600 }} title={regionLabel(b.region, netRegions)}>{regionLabel(b.region, netRegions)}</Box>
                <Box sx={{ fontSize: 12, color: "text.secondary" }}>{b.branch ? branchShort(b.branch) : "—"}</Box>
              </> },
            { id: "load", label: "Always-on load (p10, 30 d)", width: 150, align: "right", render: b => <>
                <Box>{n(Math.round(b.seatsP10 ?? 0))} seats</Box>
                <Box sx={{ fontSize: 12, color: "text.secondary" }}>{fx(b.machinesP10)} machines{b.seatsPerMachine > 0 ? ` × ${n(b.seatsPerMachine)}` : ""}</Box>
              </> },
            { id: "hourly", label: "One machine, hourly", width: 125, align: "right", render: b => <>
                <Box>{usdH(b.hourlyUsd)}</Box>
                <Box sx={{ fontSize: 12, color: "text.secondary" }}>≈ {usdMonth((b.hourlyUsd ?? 0) * HOURS_PER_MONTH)}</Box>
              </> },
            { id: "alwaysOn", label: "Always-on at hourly", width: 125, align: "right", render: b => usdMonth(b.alwaysOnMonthlyUsd) },
            { id: "dedicated", label: "Dedicated candidate", width: 125, align: "right", render: b => b.dedicatedMonthlyUsd > 0 ? usdMonth(b.dedicatedMonthlyUsd) : "—" },
            { id: "verdict", label: "Break-even", width: 360, render: verdict },
        ];
        return <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Dedicated break-even per region</Typography>
            {rows.length === 0
                ? <Typography variant="body2" color="text.secondary">No region with a scale rule yet (Scaling page).</Typography>
                : <DataTable columns={columns} rows={rows} getRowKey={b => `${b.region}|${b.branch ?? ""}|${b.ruleId ?? ""}`} sx={FIXED} />}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                The always-on load is the 10th percentile of the seats used over 30 days, in machines of the region's plan: the part of the load
                that never goes away. Paid by the hour it costs load × hourly × 730 per month; a dedicated server pays for itself once that is
                above its monthly price. Bought by hand, it comes in with Machines → Adopt machine (reserved: filled first, never destroyed).
            </Typography>
        </Box>;
    }

    renderDaily() {
        const { costs: c, netRegions } = this.state;
        const rows = [...(c.daily ?? [])].sort((a, b) => String(b.day).localeCompare(String(a.day)) || String(a.region).localeCompare(String(b.region)) || String(a.provider).localeCompare(String(b.provider)));
        // the period per region + provider
        const totals = new Map();
        for (const d of rows) {
            const k = `${d.region}|${d.provider}`;
            const t = totals.get(k) ?? { region: d.region, provider: d.provider, hours: 0, usd: 0 };
            t.hours += Number(d.hours ?? 0); t.usd += Number(d.usd ?? 0);
            totals.set(k, t);
        }
        const totalColumns = [
            { id: "region", label: "Region", width: 200, render: r => <Cut text={regionLabel(r.region, netRegions)} /> },
            { id: "provider", label: "Provider", width: 110, render: r => <Cut text={r.provider} /> },
            { id: "hours", label: "Machine-hours", width: 110, align: "right", render: r => fx(r.hours) },
            { id: "usd", label: "Spend", width: 100, align: "right", render: r => usd2(r.usd) },
        ];
        const columns = [
            { id: "day", label: "Day (UTC)", width: 110, render: r => String(r.day).slice(0, 10) },
            { id: "region", label: "Region", width: 200, render: r => <Cut text={regionLabel(r.region, netRegions)} /> },
            { id: "provider", label: "Provider", width: 110, render: r => <Cut text={r.provider} /> },
            { id: "machines", label: "Machines", width: 90, align: "right", render: r => n(r.machines) },
            { id: "hours", label: "Machine-hours", width: 110, align: "right", render: r => fx(r.hours) },
            { id: "usd", label: "Spend", width: 100, align: "right", render: r => usd2(r.usd) },
        ];
        return <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Spend per region and provider</Typography>
            {totals.size === 0
                ? <Typography variant="body2" color="text.secondary">No spend in this range.</Typography>
                : <>
                    <DataTable columns={totalColumns} rows={[...totals.values()]} getRowKey={r => `${r.region}|${r.provider}`} sx={{ ...FIXED, maxWidth: 560, mb: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Per day</Typography>
                    <DataTable columns={columns} rows={rows} getRowKey={r => `${r.day}|${r.region}|${r.provider}`} sx={{ ...FIXED, maxWidth: 760 }} />
                </>}
        </Box>;
    }

    render() {
        const { costs, days, loadedAt, stale } = this.state;
        return (
            <PageContainer maxWidth={1300}>
                <SectionHeader title="Costs">
                    <StaleHint loadedAt={loadedAt} stale={stale} />
                    <ToggleButtonGroup size="small" exclusive value={days} onChange={this.setDays}>
                        {RANGES.map(d => <ToggleButton key={d} value={d}>{d} days</ToggleButton>)}
                    </ToggleButtonGroup>
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.loader.load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                {costs == null
                    ? (stale ? <Alert severity="warning">Could not load the costs: {stale}</Alert> : <Typography variant="body2" color="text.secondary">Loading…</Typography>)
                    : <>
                        {this.renderCards()}
                        {this.renderChart()}
                        {this.renderLive()}
                        {this.renderBreakEven()}
                        {this.renderDaily()}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            Spend = machine-hours × the machine's hourly price (a reserved machine: its monthly price / 730), from the destroyed
                            machines' rows plus the live machines so far. The scaler's numbers and its spend cap are on the <Box component="span"
                            sx={{ color: "primary.main", cursor: "pointer" }} onClick={() => openPage("scaling")}>Scaling page</Box>.
                        </Typography>
                    </>}
            </PageContainer>
        );
    }
}
